import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useSelector } from "react-redux";
import {
  ChevronDown,
  Check,
  Search,
  X,
  UploadCloud,
  Loader2,
  FileText,
} from "lucide-react";

export const ORIGIN = "https://estbn.com/EstbnNew/public";

/* ------------------------------------------------------------------ */
/* Generic GET hook for dropdown data                                  */
/* ------------------------------------------------------------------ */
export function useLookup(path, { params, pick, enabled = true } = {}) {
  const { user } = useSelector((s) => s.auth);
  const token = user?.token;
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const paramsKey = JSON.stringify(params || {});

  useEffect(() => {
    if (!enabled || !token) {
      setData([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${ORIGIN}${path}`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      })
      .then((res) => {
        if (!cancelled) setData(pick(res.data?.data) || []);
      })
      .catch((err) => {
        console.error(`Error loading ${path}:`, err);
        if (!cancelled) setData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, token, paramsKey, enabled]);

  return { data, loading };
}

/* ------------------------------------------------------------------ */
/* Field wrapper                                                       */
/* ------------------------------------------------------------------ */
export function Field({ label, required, error, children, className = "" }) {
  return (
    <div className={`vc-field ${className}`}>
      {label && (
        <label className="vc-label">
          {label}
          {required && <span className="vc-req">*</span>}
        </label>
      )}
      {children}
      {error && <span className="vc-error-text">{error}</span>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Custom Select                                                       */
/* ------------------------------------------------------------------ */
export function Select({
  value,
  options = [],
  onChange,
  placeholder = "Select",
  disabled = false,
  loading = false,
  clearable = false,
  invalid = false,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  const selected = options.find((o) => String(o.value) === String(value));

  const filtered = useMemo(() => {
    if (!q.trim()) return options;
    return options.filter((o) =>
      String(o.label).toLowerCase().includes(q.trim().toLowerCase()),
    );
  }, [q, options]);

  return (
    <div
      ref={ref}
      className={`vc-select ${open ? "is-open" : ""} ${
        disabled ? "is-disabled" : ""
      } ${invalid ? "is-invalid" : ""}`}
    >
      <button
        type="button"
        className="vc-select-trigger"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
      >
        <span
          className={selected ? "vc-select-value" : "vc-select-placeholder"}
        >
          {loading ? "Loading..." : selected ? selected.label : placeholder}
        </span>
        {loading ? (
          <Loader2 size={16} className="vc-spin" />
        ) : (
          <ChevronDown size={16} className="vc-select-chevron" />
        )}
      </button>

      {open && (
        <div className="vc-select-menu">
          {options.length > 7 && (
            <div className="vc-select-search">
              <Search size={14} />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search..."
              />
            </div>
          )}

          <div className="vc-select-list" role="listbox">
            {clearable && selected && (
              <div
                role="option"
                className="vc-select-option vc-select-option--clear"
                onClick={() => {
                  onChange("");
                  setOpen(false);
                }}
              >
                <X size={13} /> Clear selection
              </div>
            )}

            {filtered.length === 0 && (
              <div className="vc-select-empty">No options found</div>
            )}

            {filtered.map((o) => {
              const isSel = String(o.value) === String(value);
              return (
                <div
                  key={o.value}
                  role="option"
                  aria-selected={isSel}
                  className={`vc-select-option ${isSel ? "is-selected" : ""}`}
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                  }}
                >
                  <span className="vc-select-option-label">{o.label}</span>
                  {isSel && <Check size={15} className="vc-select-check" />}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Multi Select (checkbox options + chips)                             */
/* ------------------------------------------------------------------ */
export function MultiSelect({
  values = [],
  options = [],
  onChange,
  placeholder = "Select",
  disabled = false,
  loading = false,
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // if the options change (e.g. company changed) close the menu
  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const isSel = (v) => values.some((x) => String(x) === String(v));
  const selectedOptions = options.filter((o) => isSel(o.value));
  const allSelected = options.length > 0 && selectedOptions.length === options.length;

  const toggle = (v) =>
    onChange(isSel(v) ? values.filter((x) => String(x) !== String(v)) : [...values, v]);

  return (
    <div
      ref={ref}
      className={`vc-select ${open ? "is-open" : ""} ${disabled ? "is-disabled" : ""}`}
    >
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        className="vc-select-trigger vc-select-trigger--multi"
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (!disabled && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
      >
        <div className="vc-chips">
          {loading ? (
            <span className="vc-select-placeholder">Loading...</span>
          ) : selectedOptions.length === 0 ? (
            <span className="vc-select-placeholder">{placeholder}</span>
          ) : (
            selectedOptions.map((o) => (
              <span className="vc-chip" key={o.value}>
                {o.label}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggle(o.value);
                  }}
                  title="Remove"
                >
                  <X size={11} />
                </button>
              </span>
            ))
          )}
        </div>
        {loading ? (
          <Loader2 size={16} className="vc-spin" />
        ) : (
          <ChevronDown size={16} className="vc-select-chevron" />
        )}
      </div>

      {open && (
        <div className="vc-select-menu">
          <div className="vc-multi-head">
            <span>
              {selectedOptions.length} of {options.length} selected
            </span>
            <button
              type="button"
              onClick={() => onChange(allSelected ? [] : options.map((o) => o.value))}
            >
              {allSelected ? "Clear all" : "Select all"}
            </button>
          </div>

          <div className="vc-select-list" role="listbox" aria-multiselectable="true">
            {options.length === 0 && (
              <div className="vc-select-empty">No options found</div>
            )}
            {options.map((o) => {
              const checked = isSel(o.value);
              return (
                <div
                  key={o.value}
                  role="option"
                  aria-selected={checked}
                  className={`vc-select-option ${checked ? "is-selected" : ""}`}
                  onClick={() => toggle(o.value)}
                >
                  <span className="vc-select-option-label">{o.label}</span>
                  <span className={`vc-mini-check ${checked ? "is-on" : ""}`}>
                    <Check size={12} />
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Modal (portal)                                                      */
/* ------------------------------------------------------------------ */
export function Modal({
  title,
  subtitle,
  icon: Icon,
  onClose,
  children,
  footer,
  width = 620,
  headerLeft,
}) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return createPortal(
    <div
      className="vc-overlay"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="vc-modal" style={{ maxWidth: width }} role="dialog">
        <div className="vc-modal-head">
          <div className="vc-modal-head-left">
            {headerLeft}
            {Icon && (
              <span className="vc-modal-icon">
                <Icon size={18} />
              </span>
            )}
            <div>
              <h3>{title}</h3>
              {subtitle && <p>{subtitle}</p>}
            </div>
          </div>
          <button type="button" className="vc-icon-btn" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        <div className="vc-modal-body">{children}</div>
        {footer && <div className="vc-modal-foot">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/* ------------------------------------------------------------------ */
/* Image uploader (multi / single) with previews                       */
/* ------------------------------------------------------------------ */
export function ImageUploader({
  files,
  onChange,
  multiple = true,
  max = 10,
  title = "Click to upload or drag & drop",
  hint = "PNG, JPG or WEBP",
  compact = false,
}) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);

  const previews = useMemo(
    () => files.map((f) => ({ file: f, url: URL.createObjectURL(f) })),
    [files],
  );
  useEffect(
    () => () => previews.forEach((p) => URL.revokeObjectURL(p.url)),
    [previews],
  );

  const addFiles = (list) => {
    const imgs = Array.from(list || []).filter((f) =>
      f.type.startsWith("image/"),
    );
    if (!imgs.length) return;
    onChange(multiple ? [...files, ...imgs].slice(0, max) : [imgs[0]]);
  };

  const removeAt = (i) => onChange(files.filter((_, idx) => idx !== i));

  return (
    <div className="vc-uploader">
      <div
        className={`vc-dropzone ${drag ? "is-drag" : ""} ${
          compact ? "is-compact" : ""
        }`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setDrag(true);
        }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDrag(false);
          addFiles(e.dataTransfer.files);
        }}
      >
        <span className="vc-dropzone-icon">
          <UploadCloud size={compact ? 18 : 22} />
        </span>
        <div>
          <strong>{title}</strong>
          <small>
            {hint}
            {multiple ? ` • up to ${max} images` : ""}
          </small>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          multiple={multiple}
          hidden
          onChange={(e) => {
            addFiles(e.target.files);
            e.target.value = "";
          }}
        />
      </div>

      {previews.length > 0 && (
        <div className="vc-thumbs">
          {previews.map((p, i) => (
            <div className="vc-thumb" key={p.url}>
              <img src={p.url} alt={p.file.name} />
              <button
                type="button"
                className="vc-thumb-remove"
                onClick={() => removeAt(i)}
                title="Remove"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Single file uploader (documents)                                    */
/* ------------------------------------------------------------------ */
const formatSize = (bytes = 0) =>
  bytes > 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

export function FileUploader({
  file,
  onChange,
  accept = ".pdf,.doc,.docx",
  title = "Click to upload a document",
  hint = "PDF, DOC or DOCX",
}) {
  const inputRef = useRef(null);

  if (file) {
    return (
      <div className="vc-file-chip">
        <span className="vc-file-chip-icon">
          <FileText size={18} />
        </span>
        <div className="vc-file-chip-info">
          <strong title={file.name}>{file.name}</strong>
          <small>{formatSize(file.size)}</small>
        </div>
        <button
          type="button"
          className="vc-icon-btn"
          onClick={() => onChange(null)}
          title="Remove"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  return (
    <div
      className="vc-dropzone is-compact"
      onClick={() => inputRef.current?.click()}
    >
      <span className="vc-dropzone-icon">
        <FileText size={18} />
      </span>
      <div>
        <strong>{title}</strong>
        <small>{hint} • one file only</small>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        hidden
        onChange={(e) => {
          onChange(e.target.files?.[0] || null);
          e.target.value = "";
        }}
      />
    </div>
  );
}
